/* Linsoul recommended products: Shopify recommendations, carousel controls and per-card add to cart (no drawer opening). */
(() => {
  'use strict';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const norm = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const fill = (template, values) => Object.entries(values).reduce((out, [k, v]) => out.split(`[${k}]`).join(v), template || '');
  const CONNECTOR_TOKENS = ['078mm2pin', '078mm', '2pin', 'mmcx', 'qdc'];

  async function fetchCards(url) {
    if (!url) return [];
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...doc.querySelectorAll('[data-lsr-track] > [data-lsr-item]')];
    } catch (error) {
      console.warn('Linsoul recommendations unavailable', error);
      return [];
    }
  }

  function limitConnector(root, card) {
    if (root.dataset.matchConnector !== 'true') return;
    const connector = norm(root.dataset.connector);
    if (!connector) return;
    const select = card.querySelector('[data-lsr-select]');
    if (!select) return;
    const options = [...select.options].filter((o) => o.value);
    const hasConnectorOptions = options.some((o) => CONNECTOR_TOKENS.some((t) => norm(o.textContent).includes(t)));
    if (!hasConnectorOptions) return;
    const matching = options.filter((o) => norm(o.textContent).includes(connector));
    if (!matching.length) return;
    options.forEach((o) => {
      if (!matching.includes(o)) o.remove();
    });
    if (matching.length === 1 && !matching[0].disabled) {
      select.value = matching[0].value;
      select.dispatchEvent(new Event('change'));
    }
  }

  function initCarousel(root) {
    const track = root.querySelector('[data-lsr-track]');
    const items = () => [...track.querySelectorAll('[data-lsr-item]')];
    const prev = root.querySelector('[data-lsr-prev]');
    const next = root.querySelector('[data-lsr-next]');
    const range = root.querySelector('[data-lsr-range]');
    const dots = root.querySelector('[data-lsr-dots]');
    const L = root.dataset;

    const metrics = () => {
      const list = items();
      const first = list[0];
      if (!first) return { list, step: 1, perView: 1, start: 0 };
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor((track.clientWidth + gap + 1) / step));
      const start = Math.round(track.scrollLeft / step);
      return { list, step, perView, start };
    };

    const go = (index) => {
      const { list, step } = metrics();
      const target = Math.max(0, Math.min(list.length - 1, index));
      track.scrollTo({ left: target * step, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    const update = () => {
      const { list, perView, start } = metrics();
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      const first = atEnd ? Math.max(0, list.length - perView) : start;
      const last = Math.min(list.length, first + perView);
      if (range) range.textContent = fill(L.labelRange, { start: first + 1, end: last, total: list.length });
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = atEnd;
      const pages = Math.ceil(list.length / perView);
      const page = atEnd ? pages - 1 : Math.floor(first / perView);
      if (dots) {
        if (dots.childElementCount !== pages) {
          dots.replaceChildren(...Array.from({ length: pages }, (_, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'lsr-dot';
            dot.setAttribute('aria-label', fill(L.labelPage, { n: i + 1 }));
            dot.addEventListener('click', () => go(i * metrics().perView));
            return dot;
          }));
        }
        [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
        dots.hidden = pages < 2;
      }
      root.querySelector('.lsr-controls').hidden = list.length <= perView;
    };

    items().forEach((item, i, list) => {
      item.setAttribute('aria-roledescription', 'slide');
      item.setAttribute('aria-label', fill(L.labelSlide, { n: i + 1, total: list.length }));
    });

    prev?.addEventListener('click', () => { const m = metrics(); go(m.start - m.perView); });
    next?.addEventListener('click', () => { const m = metrics(); go(m.start + m.perView); });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      const m = metrics();
      const map = { ArrowLeft: m.start - 1, ArrowRight: m.start + 1, Home: 0, End: m.list.length - 1 };
      if (!(event.key in map)) return;
      event.preventDefault();
      go(map[event.key]);
    });
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    }, { passive: true });
    new ResizeObserver(update).observe(track);
    update();
  }

  function initCards(root) {
    const L = root.dataset;
    root.querySelectorAll('[data-lsr-card]').forEach((card) => {
      const select = card.querySelector('[data-lsr-select]');
      const price = card.querySelector('[data-lsr-price]');
      const button = card.querySelector('[data-lsr-add]');
      const label = card.querySelector('[data-lsr-add-label]');
      const status = card.querySelector('[data-lsr-status]');
      let busy = false;

      const setStatus = (text, withCart) => {
        status.replaceChildren();
        if (!text) return;
        status.append(document.createTextNode(text));
        if (withCart) {
          const view = document.createElement('a');
          view.href = (window.Shopify?.routes?.root || '/') + 'cart';
          view.className = 'lsr-view-cart';
          view.textContent = L.labelViewCart;
          view.addEventListener('click', (event) => {
            const drawer = document.querySelector('m-cart-drawer');
            if (drawer && window.MinimogSettings?.enable_cart_drawer && typeof drawer.open === 'function') {
              event.preventDefault();
              drawer.open(view);
            }
          });
          status.append(' ', view);
        }
      };

      select?.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        price.textContent = option && option.value ? option.dataset.price : price.dataset.from;
        select.removeAttribute('aria-invalid');
        setStatus('');
      });
      limitConnector(root, card);

      button.addEventListener('click', async () => {
        if (busy) return;
        const id = select ? select.value : button.dataset.variantId;
        if (!id) {
          select?.setAttribute('aria-invalid', 'true');
          select?.focus();
          setStatus(L.labelChoose);
          return;
        }
        busy = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        label.textContent = L.labelAdding;
        setStatus('');
        try {
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            setStatus(result.description || result.message || L.labelError);
            return;
          }
          setStatus(L.labelAdded, true);
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
          const drawer = document.querySelector('m-cart-drawer');
          if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
        } catch (error) {
          console.warn(error);
          setStatus(L.labelError);
        } finally {
          busy = false;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          label.textContent = L.labelAdd;
        }
      });
    });
  }

  async function initSection(root) {
    if (root.dataset.lsrReady) return;
    root.dataset.lsrReady = '1';
    const track = root.querySelector('[data-lsr-track]');
    if (!track.children.length) {
      let cards = await fetchCards(root.dataset.url);
      if (!cards.length) {
        const legacy = root.querySelector('template[data-lsr-legacy]');
        if (legacy) cards = [...legacy.content.querySelectorAll('[data-lsr-item]')];
      }
      if (!cards.length) cards = await fetchCards(root.dataset.fallbackUrl);
      if (!cards.length) return;
      track.replaceChildren(...cards.map((c) => document.importNode(c, true)));
    }
    root.hidden = false;
    initCards(root);
    initCarousel(root);
    document.dispatchEvent(new CustomEvent('linsoul:sections-changed'));
  }

  const init = () => document.querySelectorAll('[data-lsr]').forEach(initSection);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
